import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore, collection, doc, getDocs, getDoc, addDoc, query, where, orderBy, limit, startAfter, DocumentSnapshot, QueryConstraint } from 'firebase/firestore';
import { Course, Banner } from '@/types/data';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

let app: FirebaseApp;
let db: Firestore;

// Initialize Firebase
export function initializeFirebase(): { app: FirebaseApp; db: Firestore } {
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }
  
  if (!db) {
    db = getFirestore(app);
  }
  
  return { app, db };
}

// Get Firestore instance
export function getFirestoreInstance(): Firestore {
  if (!db) {
    initializeFirebase();
  }
  return db;
}

// Get courses collection
export function getCoursesCollection() {
  const firestore = getFirestoreInstance();
  return collection(firestore, 'courses');
}

// Helper function to convert Firestore document to Course
export function convertFirestoreDocToCourse(doc: DocumentSnapshot): Course | null {
  if (!doc.exists()) return null;
  
  const data = doc.data();
  return {
    id: data.id,
    name: data.name,
    image: data.image,
    price: data.price,
    price_promo: data.price_promo,
    teacher: data.teacher,
    time: data.time,
    number_of_lessons: data.number_of_lessons,
    number_of_student: data.number_of_student,
    duration: data.duration,
    description: data.description,
    slug_url: data.slug_url,
    category: data.category,
    category_slug: data.category_slug,
    event_start_date: data.event_start_date,
    event_end_date: data.event_end_date,
    is_event_active: data.is_event_active,
    descriptions_khoahoc: data.descriptions_khoahoc,
    video_url: data.video_url,
    _createdAt: data._createdAt?.toDate(),
    _isNew: data._isNew,
  } as Course;
}

// Helper function to convert Course to Firestore document data
export function convertCourseToFirestoreData(course: Course): any {
  const data: any = {
    id: course.id,
    name: course.name,
    image: course.image,
    price: course.price,
    price_promo: course.price_promo,
    teacher: course.teacher,
    time: course.time,
    number_of_lessons: course.number_of_lessons,
    number_of_student: course.number_of_student,
    duration: course.duration,
    description: course.description,
    slug_url: course.slug_url,
    category: course.category,
    category_slug: course.category_slug,
    event_start_date: course.event_start_date,
    event_end_date: course.event_end_date,
    is_event_active: course.is_event_active,
    descriptions_khoahoc: course.descriptions_khoahoc,
    _createdAt: course._createdAt ? new Date(course._createdAt) : new Date(),
    _isNew: course._isNew || false,
  };

  // Only include video_url if it's not undefined
  if (course.video_url !== undefined) {
    data.video_url = course.video_url;
  }

  // Remove any undefined keys to avoid Firestore invalid data errors
  Object.keys(data).forEach((key) => {
    if (data[key] === undefined) {
      delete data[key];
    }
  });

  return data;
}

// Get all courses with optional filtering and sorting
export async function getCoursesFromFirestore(
  limitCount?: number,
  categorySlug?: string,
  searchQuery?: string,
  page?: number,
  pageSize?: number
): Promise<{ courses: Course[]; total?: number; lastDoc?: DocumentSnapshot }> {
  const coursesCollection = getCoursesCollection();
  let queryConstraints: QueryConstraint[] = [];

  // Add category filter
  if (categorySlug) {
    queryConstraints.push(where('category_slug', '==', categorySlug));
  }

  // Add search filter
  if (searchQuery) {
    queryConstraints.push(where('name', '>=', searchQuery));
    queryConstraints.push(where('name', '<=', searchQuery + '\uf8ff'));
  }

  // Add ordering - only if we have other constraints to avoid index issues
  if (queryConstraints.length > 0) {
    queryConstraints.push(orderBy('id', 'desc'));
  }

  // Add pagination
  if (page && pageSize) {
    const offset = (page - 1) * pageSize;
    // Note: Firestore doesn't support offset, so we'll use startAfter for pagination
    // This is a simplified version - in production you'd want to implement proper cursor-based pagination
  }

  // Add limit
  if (limitCount) {
    queryConstraints.push(limit(limitCount));
  }

  const q = query(coursesCollection, ...queryConstraints);
  const querySnapshot = await getDocs(q);
  const courses: Course[] = [];
  let lastDoc: DocumentSnapshot | undefined;

  querySnapshot.forEach((doc) => {
    const course = convertFirestoreDocToCourse(doc);
    if (course) {
      courses.push(course);
    }
    lastDoc = doc;
  });

  // Sort courses by id descending (newest first) if no ordering was applied in query
  if (queryConstraints.length === 0 || !queryConstraints.some(c => c.type === 'orderBy')) {
    courses.sort((a, b) => b.id - a.id);
  }
  return { courses, lastDoc };
}

// Get course by slug
export async function getCourseBySlugFromFirestore(slug: string): Promise<Course | null> {
  const coursesCollection = getCoursesCollection();
  const q = query(coursesCollection, where('slug_url', '==', slug), limit(1));
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) return null;
  
  return convertFirestoreDocToCourse(querySnapshot.docs[0]);
}

// Get courses by teacher slug
export async function getCoursesByTeacherSlugFromFirestore(teacherSlug: string): Promise<Course[]> {
  const coursesCollection = getCoursesCollection();
  const q = query(
    coursesCollection, 
    where('teacher.name_to_slug', '==', teacherSlug),
    // orderBy('_createdAt', 'desc'),
    // orderBy('id', 'desc')
  );
  const querySnapshot = await getDocs(q);
  
  const courses: Course[] = [];
  querySnapshot.forEach((doc) => {
    const course = convertFirestoreDocToCourse(doc);
    if (course) {
      courses.push(course);
    }
  });

  return courses;
}

// Add courses to Firestore
export async function addCoursesToFirestore(courses: Course[]): Promise<void> {
  const coursesCollection = getCoursesCollection();
  
  // Convert courses to Firestore format
  const firestoreData = courses.map(convertCourseToFirestoreData);
  
  // Add documents to Firestore
  for (const data of firestoreData) {
    await addDoc(coursesCollection, data);
  }
}

// Get total count of courses (for pagination)
export async function getCoursesCountFromFirestore(categorySlug?: string, searchQuery?: string): Promise<number> {
  const coursesCollection = getCoursesCollection();
  let queryConstraints: QueryConstraint[] = [];

  if (categorySlug) {
    queryConstraints.push(where('category_slug', '==', categorySlug));
  }

  if (searchQuery) {
    queryConstraints.push(where('name', '>=', searchQuery));
    queryConstraints.push(where('name', '<=', searchQuery + '\uf8ff'));
  }

  const q = query(coursesCollection, ...queryConstraints);
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.size;
}

// Get banners collection
export function getBannersCollection() {
  const firestore = getFirestoreInstance();
  return collection(firestore, 'banner');
}

// Get all banners from Firestore
export async function getBannersFromFirestore(): Promise<Banner[]> {
  const bannersCollection = getBannersCollection();
  const q = query(bannersCollection, orderBy('order', 'asc'));
  const querySnapshot = await getDocs(q);
  
  const banners: Banner[] = [];
  querySnapshot.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    banners.push({
      id: docSnapshot.id,
      imageUrl: data.imageUrl,
      alt: data.alt,
      order: data.order,
      _createdAt: data._createdAt?.toDate(),
      _updatedAt: data._updatedAt?.toDate(),
    } as Banner);
  });
  
  return banners;
}
